import { useCallback } from "react";
import invariant from "tiny-invariant";
import { CommandStatus, useCommand } from "@lookiero/messaging-react";
import { Logger } from "@lookiero/sty-psp-logging";
import { NotificationLevel, useCreateToastNotification } from "@lookiero/sty-psp-notifications";
import { submitCheckout } from "../../../../domain/checkout/command/submitCheckout";
import { MESSAGING_CONTEXT_ID } from "../../../delivery/baseBootstrap";
import { I18nMessages } from "../../../ui/i18n/i18n";

interface SubmitFunction {
  (): Promise<boolean>;
}

type UseSubmitCheckout = [submit: SubmitFunction, status: CommandStatus];

interface UseSubmitCheckoutFunctionArgs {
  readonly checkoutId?: string;
  readonly logger: Logger;
}

interface UseSubmitCheckoutFunction {
  (args: UseSubmitCheckoutFunctionArgs): UseSubmitCheckout;
}

const useSubmitCheckout: UseSubmitCheckoutFunction = ({ checkoutId, logger }) => {
  const [commandBus, status] = useCommand({ contextId: MESSAGING_CONTEXT_ID });
  const [createNotification] = useCreateToastNotification({ contextId: MESSAGING_CONTEXT_ID, logger });

  const submit = useCallback(async () => {
    try {
      // Missing checkoutId here is a programming error, not a runtime condition the user can hit:
      // folding it into the same generic-error toast as a backend failure is fine because
      // logger.captureException still reports it distinctly for monitoring.
      invariant(checkoutId, "CheckoutId must be defined in order to submit checkout");

      await commandBus(submitCheckout({ aggregateId: checkoutId }));

      return true;
    } catch (error) {
      logger.captureException(error as Error);
      createNotification({
        level: NotificationLevel.ERROR,
        bodyI18nKey: I18nMessages.TOAST_GENERIC_ERROR,
      });

      return false;
    }
  }, [checkoutId, commandBus, createNotification, logger]);

  return [submit, status];
};

export { useSubmitCheckout };
